// deviceCapability.ts
// Lightweight device capability checker for development insights

export interface DeviceCapabilityInfo {
  score: number;
  tier: 'low' | 'medium' | 'high';
  ram: number | null; // in GB
  cores: number | null;
  gpu: string | null;
  storage: { quota: number | null; usage: number | null }; // in GB
}

// Global flag to ensure single execution per page load
let hasLoggedDeviceTier = false;

/**
 * Safely get device memory information
 * @returns RAM in GB or null if unavailable
 */
function getMemoryInfo(): number | null {
  if (typeof navigator === 'undefined') return null;
  
  try {
    // @ts-ignore - deviceMemory is not in TypeScript standard types
    const memory = navigator.deviceMemory;
    return typeof memory === 'number' && memory > 0 ? memory : null;
  } catch {
    return null;
  }
}

/**
 * Safely get CPU core count
 * @returns Number of cores or null if unavailable
 */
function getCoreCount(): number | null {
  if (typeof navigator === 'undefined') return null;
  
  try {
    const cores = navigator.hardwareConcurrency;
    return typeof cores === 'number' && cores > 0 ? cores : null;
  } catch {
    return null;
  }
}

/**
 * Safely get GPU information
 * @returns GPU vendor/renderer string or null if unavailable
 */
function getGPUInfo(): string | null {
  if (typeof navigator === 'undefined') return null;
  
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        return `${vendor} - ${renderer}`;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Safely get storage information
 * @returns Storage quota and usage in GB or null if unavailable
 */
async function getStorageInfo(): Promise<{ quota: number | null; usage: number | null }> {
  if (typeof navigator === 'undefined' || !navigator.storage) {
    return { quota: null, usage: null };
  }
  
  try {
    const estimate = await navigator.storage.estimate();
    return {
      quota: estimate.quota ? Math.round(estimate.quota / (1024 ** 3) * 100) / 100 : null, // Convert to GB
      usage: estimate.usage ? Math.round(estimate.usage / (1024 ** 3) * 100) / 100 : null // Convert to GB
    };
  } catch {
    return { quota: null, usage: null };
  }
}

/**
 * Calculate device capability score (0-100)
 * @param capabilities Raw capability data
 * @returns Score between 0-100
 */
function calculateScore(capabilities: {
  ram: number | null;
  cores: number | null;
  gpu: string | null;
  storage: { quota: number | null; usage: number | null };
}): number {
  let score = 0;
  let factors = 0;

  // RAM scoring (0-30 points)
  if (capabilities.ram !== null) {
    factors++;
    if (capabilities.ram >= 16) score += 30;
    else if (capabilities.ram >= 8) score += 20;
    else if (capabilities.ram >= 4) score += 10;
    else score += 5;
  }

  // CPU cores scoring (0-30 points)
  if (capabilities.cores !== null) {
    factors++;
    if (capabilities.cores >= 16) score += 30;
    else if (capabilities.cores >= 8) score += 20;
    else if (capabilities.cores >= 4) score += 10;
    else score += 5;
  }

  // GPU scoring (0-20 points)
  if (capabilities.gpu !== null) {
    factors++;
    const gpu = capabilities.gpu.toLowerCase();
    if (gpu.includes('nvidia') || gpu.includes('amd') || gpu.includes('radeon')) score += 20;
    else if (gpu.includes('intel')) score += 10;
    else score += 5;
  }

  // Storage scoring (0-20 points)
  if (capabilities.storage.quota !== null) {
    factors++;
    if (capabilities.storage.quota >= 50) score += 20;
    else if (capabilities.storage.quota >= 10) score += 15;
    else if (capabilities.storage.quota >= 5) score += 10;
    else score += 5;
  }

  // Normalize score if we have factors
  return factors > 0 ? Math.round((score / (factors * 10)) * 100) : 50; // Default to 50 if no data
}

/**
 * Determine device tier based on score
 * @param score Device capability score (0-100)
 * @returns Tier classification
 */
function determineTier(score: number): 'low' | 'medium' | 'high' {
  if (score >= 75) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

/**
 * Gather device capability information
 * @returns Promise resolving to device capability info
 */
export async function getDeviceCapability(): Promise<DeviceCapabilityInfo> {
  const ram = getMemoryInfo();
  const cores = getCoreCount();
  const gpu = getGPUInfo();
  const storage = await getStorageInfo();

  const capabilities = { ram, cores, gpu, storage };
  const score = calculateScore(capabilities);
  const tier = determineTier(score);

  return {
    score,
    tier,
    ram,
    cores,
    gpu,
    storage
  };
}

/**
 * Log device tier information once per page load in development only
 */
export async function logDeviceTier(): Promise<void> {
  // Only run in development and browser environment
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return;
  }

  // Ensure single execution per page load
  if (hasLoggedDeviceTier) {
    return;
  }
  hasLoggedDeviceTier = true;

  try {
    const deviceInfo = await getDeviceCapability();
    
    console.group('📊 Device Tier');
    console.log('Score:', deviceInfo.score);
    console.log('Tier:', deviceInfo.tier);
    console.log('RAM:', deviceInfo.ram !== null ? `${deviceInfo.ram}GB` : 'N/A');
    console.log('Cores:', deviceInfo.cores !== null ? deviceInfo.cores : 'N/A');
    console.log('GPU:', deviceInfo.gpu || 'N/A');
    console.log('Storage:', 
      deviceInfo.storage.quota !== null ? 
        `${deviceInfo.storage.quota}GB (${deviceInfo.storage.usage || 0}GB used)` : 
        'N/A'
    );
    console.groupEnd();
  } catch (error) {
    // Silent fail - device capability check should never break the app
    console.debug('Device capability check failed:', error);
  }
}

// Export for testing purposes
export const __testing__ = {
  calculateScore,
  determineTier,
  getMemoryInfo,
  getCoreCount,
  getGPUInfo,
  getStorageInfo,
  hasLoggedDeviceTier: () => hasLoggedDeviceTier
};
